import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Moment } from "moment";
import {
  calendarService,
  subscriptionService,
  taskService,
} from "@miplatform/api";
import {
  ATTEMPT_STATUSES,
  CALENDAR_ZOOM,
  HTTP_STATUSES,
  SUBMIT_STATUSES,
} from "@miplatform/constants";
import { useComponentState, useDebounce } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  CalendarEventType,
  CalendarType,
  IApiRequest,
  IApiResponse,
  IApiResponseBase,
  IAttempt,
  IAttemptListItemResponseForWorkOrder,
  IAttemptListResponse,
  IAttemptTrayConfig,
  IAttemptUpdateSubscriptionPayload,
  IAvailabilityLog,
  ICalendarConfig,
  IDateRangeISO,
  ITenantHolidayResponse,
  IUserListItemResponse,
} from "@miplatform/types";
import {
  addWorkDays,
  bugsnag,
  convertArrayToMap,
  createUUID,
  getBusinessDate,
  getDate,
  getDateISO,
  getGUIDArray,
  getItemLocal,
  isSameOrAfter,
  isSameOrBefore,
} from "@miplatform/utils";
import { useGlobalManagerApproval } from "@mibuildweb/components";
import { useSession } from "@mibuildweb/hooks";
import { loadCalendarEvents } from "./utils/loadCalendarEvents";
import { useUsers } from "../../hooks/useUser/useUsers";
import { useStoredCalendar, useStoredCalendarSet } from "../../stores";
import { FOLLOW_UP_TRAY_FILTERS } from "../../views/Config/Calendars/ConfigCalendarSetList.constants";
import {
  CalendarAttemptEvents,
  SchedulerViewType,
} from "./CalendarProvider.types";

const loadEventsUUID = createUUID();
const SELECTED_USER_DAYS_RANGE = 5;

const getStartDate = ({
  calendarType,
  date,
  dateRange,
}: {
  calendarType: CalendarType | undefined;
  date: Moment;
  dateRange: IDateRangeISO;
}) => {
  if (calendarType === CalendarType.Kanban) {
    return getDate(dateRange.startDate);
  }

  return date;
};

const getEndDate = ({
  calendarType,
  date,
  dateRange,
  isUserSelected,
  isAssigneeMode,
}: {
  calendarType: CalendarType | undefined;
  date: Moment;
  dateRange: IDateRangeISO;
  isUserSelected: boolean;
  isAssigneeMode: boolean;
}) => {
  if (calendarType === CalendarType.Kanban) {
    return getDate(dateRange.endDate);
  }

  if (isUserSelected || isAssigneeMode) {
    return addWorkDays(SELECTED_USER_DAYS_RANGE, date);
  }

  return undefined;
};

const isTenantHolidayForCalendar = (
  tenantHoliday: ITenantHolidayResponse,
  calendarId: string | undefined
) =>
  !!calendarId &&
  tenantHoliday.calendarSets.some(({ calendarConfigs }) =>
    calendarConfigs.some(({ id }) => id === calendarId)
  );

export const useCalendarProviderViewModel = ({
  calendarSetId = null,
  calendarId = null,
  selectedUser = null,
}: IUseCalendarProviderViewModelProps) => {
  const stateManager = useComponentState();
  const { session } = useSession();
  const { executeApiRequest } = useGlobalManagerApproval();

  const { calendarSet } = useStoredCalendarSet(calendarSetId);

  const { calendarConfig } = useStoredCalendar(calendarSetId, calendarId);
  const { config: calendarSetConfig } = calendarSet ?? {};
  const { assignableUsers = [], schedulerUsers = [] } = calendarSetConfig ?? {};

  const activeCalendarId = useMemo(() => calendarConfig?.id, [calendarConfig]);

  const [users, setUsers] = useState<IUserListItemResponse[]>([]);
  const [events, setEvents] = useState<CalendarAttemptEvents>({});
  const [highlightedAttempts, setHighlightedAttempts] = useState<{
    [index: string]: boolean;
  }>({});
  const [selectedAttempt, setSelectedAttempt] = useState<IAttempt | null>(null);
  const [zoom, setZoom] = useState(1);
  const [date, setDate] = useState<Moment>(getBusinessDate());
  const [tenantHolidays, setTenantHolidays] = useState<
    ITenantHolidayResponse[]
  >([]);
  const [dateRange, setDateRange] = useState<IDateRangeISO>({
    startDate: getDateISO(getBusinessDate()),
    endDate: getDateISO(getBusinessDate()),
  });
  const [trayBuckets, setTrayBuckets] = useState<IAttemptTrayConfig[]>([]);
  const [calendarCounts, setCalendarCounts] = useState<Record<string, string>>(
    {}
  );
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const activeTenantHolidays = useMemo(
    () =>
      tenantHolidays.filter((holiday) =>
        isTenantHolidayForCalendar(holiday, activeCalendarId)
      ),
    [tenantHolidays, activeCalendarId]
  );

  const { getUsers } = useUsers();

  const [verticalTabActiveFilter, setVerticalTabActiveFilter] = useState<
    string | null
  >(null);

  const activeViewRef = useRef<SchedulerViewType | null>(
    SchedulerViewType.Scheduler
  );

  const setActiveView = useCallback((view: SchedulerViewType) => {
    activeViewRef.current = view;
  }, []);

  const onDateRangeChange = useCallback((newDateRange: IDateRangeISO) => {
    setDateRange(newDateRange);
  }, []);

  const highlightAttempts = useCallback((eventId: string | string[]) => {
    setHighlightedAttempts((prev) => {
      const newState = { ...prev };
      if (Array.isArray(eventId)) {
        eventId.forEach((id) => {
          newState[id] = true;
        });
      } else {
        newState[eventId] = true;
      }
      return newState;
    });
  }, []);

  const unhighlightAttempts = useCallback((eventId: string | string[]) => {
    setHighlightedAttempts((prev) => {
      const newState = { ...prev };
      if (Array.isArray(eventId)) {
        eventId.forEach((id) => {
          delete newState[id];
        });
      } else {
        delete newState[eventId];
      }
      return newState;
    });
  }, []);

  const getAvailabilityLogs: () => Promise<IAvailabilityLog[]> =
    useCallback(async () => {
      let filters;

      if (selectedUser) {
        filters = {
          userId: { eq: { value: selectedUser?.id, type: "guid" } },
          to: { gt: getDate(date).startOf("day").toDate() },
          from: {
            lt: addWorkDays(5, date).endOf("day").toDate(),
          },
        };
      } else {
        filters = {
          userId: { in: calendarConfig?.assignableUserIds },
          to: { gt: getDate(date).startOf("day").toDate() },
          from: { lt: addWorkDays(5, date).endOf("day").toDate() },
        };
      }

      const { data, status } =
        await subscriptionService.users.getUserAvailabilityLogs({
          filters,
        });

      if (status !== HTTP_STATUSES.OK) return [];

      return data;
    }, [dateRange, date, calendarConfig?.assignableUserIds, selectedUser]);

  const loadUsers = useCallback(async () => {
    if (!calendarConfig) {
      return;
    }

    const { data, status } = await getUsers({
      filters: {
        id: {
          in:
            calendarConfig?.assignableUsers?.map(({ id }) => ({
              type: "guid",
              value: id,
            })) ?? [],
        },
      },
      sort: "firstName asc",
    });

    if (status === HTTP_STATUSES.OK) {
      if (calendarConfig?.calendarType === CalendarType.Kanban) {
        setUsers(data);
        return;
      }
      const availabilityLog = await getAvailabilityLogs();
      data.forEach((user: IUserListItemResponse) => {
        user.availabilityLog = availabilityLog.filter(
          (log) => log.userId === user.id
        );
      });

      setUsers(data);
    }
  }, [calendarConfig, getAvailabilityLogs]);

  /**
   * TODO: Improve the speed of this getTrays call. Super slow. On average I am seeing 4+ seconds to load
   * const startTime = performance.now();
   * await getTrayDataForCalendar();
   * console.log(
   *  `Took ${(performance.now() - startTime).toFixed(2)}ms to get Trays`
   * )
   *
   * Unified tray data fetching - used by both Kanban buckets and HoldsList
   * @returns void
   */
  const getTrayDataForCalendar = async () => {
    if (
      !calendarSet?.config?.trays?.length ||
      !calendarSet?.config.calendarConfigs.length
    ) {
      setTrayBuckets([]);
      return;
    }

    const attachedTrayCalendarMap = Object.values(calendarSet.calendars).reduce(
      (acc: Record<string, string[]>, calendar) => {
        if (calendar.config.attachedTray) {
          if (!acc[calendar.config.attachedTray.id]) {
            acc[calendar.config.attachedTray.id] = [];
          }
          acc[calendar.config.attachedTray.id].push(calendar.config.id);
        }
        return acc;
      },
      {}
    );

    const orderTemplateIds =
      calendarSet.config.workTemplateBases?.map((template) => template.id) ??
      [];

    const trayBuckets: IAttemptTrayConfig[] = [];
    const allTrayData:
      | IAttemptListResponse[]
      | IAttemptListItemResponseForWorkOrder[] = [];
    const isWorkOrderEventType =
      calendarSet.config.eventType === CalendarEventType.WorkOrder;
    const assignmentPath = isWorkOrderEventType ? "workOrder" : "order";
    const templateIdPath = isWorkOrderEventType
      ? "workOrderTemplateId"
      : "orderTemplateId";
    const getAttempts = isWorkOrderEventType
      ? taskService.attempts.getAllWorkOrders
      : taskService.attempts.getAll;

    const trayPromises = calendarSet.config.trays.map(
      async (tray: IAttemptTrayConfig) => {
        const filterStringParts = [];
        if (tray?.filter) {
          filterStringParts.push(`(${tray?.filter})`);
        }

        if (orderTemplateIds.length) {
          filterStringParts.push(
            `assignment/${assignmentPath}/${templateIdPath} in (${orderTemplateIds.join(
              ","
            )})`
          );
        }

        // TODO : yuck, but elsewhere we are matching on filter to determine type like in the "Tray Type" dropdown in calendar configs
        // this will filter all Follow-Up trays to limit them to the attached Hold Reason Tabs/Calendar Ids, to prevent
        // trays that have no attached calendars (hold reasons) from finding orders from ALL calendars instead of none.
        if (tray?.filter === FOLLOW_UP_TRAY_FILTERS) {
          const trayAttachedCalendarIds =
            attachedTrayCalendarMap[tray.id] ?? [];
          if (trayAttachedCalendarIds.length < 1) {
            // a follow up tray with no attached calendars
            // this tray will always be empty, so skip the API call
            return {
              tray,
              data: [],
            };
          }
          filterStringParts.push(
            `assignment/${assignmentPath}/holdCalendarId in (${trayAttachedCalendarIds.join(
              ","
            )})`
          );
        }

        const filterString = filterStringParts.join(" and ");

        try {
          const response = await getAttempts({
            filters: filterString,
            ...(!isWorkOrderEventType && {
              params: {
                includeWorkOrders: true,
              },
            }),
            ...(tray.sort &&
              typeof tray.sort === "string" && { sort: tray.sort }),
          });

          if (response.status === HTTP_STATUSES.OK) {
            return {
              tray,
              data: response.data.map((attempt: IAttemptListResponse) => {
                return {
                  ...attempt,
                  isRequestedInDateRange:
                    attempt.assignment?.order?.requestedStartDate &&
                    isSameOrAfter(
                      attempt.assignment?.order?.requestedStartDate,
                      dateRange.startDate
                    ) &&
                    isSameOrBefore(
                      attempt.assignment?.order?.requestedStartDate,
                      dateRange.endDate ?? dateRange.startDate
                    ),
                };
              }),
            };
          }
        } catch (trayError) {
          bugsnag.setContext(
            "useCalendarProviderViewModel::getTrayDataForCalendar"
          );
          bugsnag.notify(
            trayError instanceof Error
              ? trayError
              : new Error(String(trayError))
          );
        }

        return {
          tray,
          data: [],
        };
      }
    );

    try {
      const trayResults = await Promise.all(trayPromises);
      trayResults.forEach(({ tray, data }) => {
        allTrayData.push(...data);

        trayBuckets.push({
          id: tray.id,
          isActive: tray.isActive,
          isDefault: tray.isDefault,
          name: tray.name,
          collapsible: tray.collapsible,
          displayOrder: tray.displayOrder,
          headerColor: tray.headerColor,
          filter: tray.filter,
          headerBackgroundColor: tray.headerBackgroundColor,
          contentBackgroundColor: tray.contentBackgroundColor,
          countBackgroundColor: tray.countBackgroundColor,
          countColor: tray.countColor,
          hideWhenEmpty: tray.hideWhenEmpty,
          data,
        });
      });

      trayBuckets.sort((a, b) => a.displayOrder - b.displayOrder);
    } catch (error) {
      bugsnag.setContext(
        "useCalendarProviderViewModel::getTrayDataForCalendar"
      );
      bugsnag.notify(error instanceof Error ? error : new Error(String(error)));
    }

    setTrayBuckets(trayBuckets);
  };

  const resetTrays = () => {
    setTrayBuckets([]);
  };

  const isScheduler = useMemo(() => {
    return !!(
      session?.userId &&
      schedulerUsers?.some((user) => user.id === session.userId)
    );
  }, [schedulerUsers, session?.userId]);

  const assigneeModeUser = useMemo(() => {
    const isAssignee =
      session?.userId &&
      assignableUsers?.some((user) => user.id === session.userId);

    const isAssigneeMode = isAssignee && !isScheduler;

    if (!isAssigneeMode || !assignableUsers || !session?.userId) {
      return null;
    }
    const assigneeModeUser = assignableUsers?.find(
      (user) => user.id === session?.userId
    );

    return assigneeModeUser;
  }, [assignableUsers, session?.userId, isScheduler]);

  const calendarSetConfigRef = useRef(calendarSetConfig);
  const calendarConfigRef = useRef(calendarConfig);

  calendarSetConfigRef.current = calendarSetConfig;
  calendarConfigRef.current = calendarConfig;

  const handleLoadEvents = useCallback(async () => {
    const currentCalendarSetConfig = calendarSetConfigRef.current;
    const currentCalendarConfig = calendarConfigRef.current;

    if (!currentCalendarSetConfig || !currentCalendarConfig) {
      return;
    }

    stateManager.abort(loadEventsUUID);
    const startDate = getStartDate({
      calendarType: currentCalendarConfig.calendarType,
      date,
      dateRange,
    });
    const endDate = getEndDate({
      calendarType: currentCalendarConfig.calendarType,
      date,
      dateRange,
      isUserSelected: !!selectedUser,
      isAssigneeMode: !!assigneeModeUser,
    });

    await stateManager.run(async () => {
      setEvents({});
      const { data: newEvents, status } = await loadCalendarEvents({
        calendarSetConfig: currentCalendarSetConfig,
        calendarConfig: currentCalendarConfig,
        startDate,
        endDate,
        userIds: selectedUser ? [selectedUser.id] : null,
        search: debouncedSearch,
      });

      if (status === HTTP_STATUSES.OK) {
        setEvents(convertArrayToMap(newEvents, "id"));
      }
    }, loadEventsUUID);
  }, [
    calendarSetConfig?.id,
    calendarConfig?.id,
    calendarConfig?.calendarType,
    date,
    dateRange.startDate,
    dateRange.endDate,
    selectedUser,
    debouncedSearch,
    assigneeModeUser,
  ]);

  const onAttemptsUpdate = useCallback(
    (payload: IAttemptUpdateSubscriptionPayload) => {
      setEvents((prev) => {
        const newEvents = { ...prev };
        for (const updatedEvent of payload) {
          const {
            attemptUpdatedEvent: { attemptId: updatedAttemptId },
          } = updatedEvent;

          const currentEvent = newEvents[updatedAttemptId];

          newEvents[updatedAttemptId] = {
            ...currentEvent,
            ...updatedEvent.attempt,
            assignedUserId: updatedEvent.attempt.assignedUserId ?? undefined,
            assignedUserName:
              updatedEvent.attempt.assignedUserName ?? undefined,
          };
        }

        return newEvents;
      });

      if (calendarSetConfig?.eventType === CalendarEventType.WorkOrder) {
        const prevHoldEvents = new Set(
          payload
            .filter(
              ({ attempt: { previousStatus, attemptStatus } }) =>
                attemptStatus !== ATTEMPT_STATUSES.HOLD &&
                attemptStatus !== ATTEMPT_STATUSES.NEW &&
                (previousStatus === ATTEMPT_STATUSES.HOLD ||
                  previousStatus === ATTEMPT_STATUSES.NEW)
            )
            .map(({ attempt: { id } }) => id)
        );

        if (prevHoldEvents.size === 0) {
          return;
        }

        setTrayBuckets((prev) => {
          const filteredTrays = [...prev].map((tray) => ({
            ...tray,
            data: tray.data?.filter((event) => !prevHoldEvents.has(event.id)),
          }));

          for (let i = 0; i < filteredTrays.length; i++) {
            if (filteredTrays[i].data?.length !== prev[i].data?.length) {
              return filteredTrays;
            }
          }

          return prev;
        });
      }
    },
    [calendarSetConfig, calendarConfig?.id]
  );

  const unscheduleAttempt = useCallback(
    (attempt: IAttemptListItemResponseForWorkOrder) => {
      stateManager.run(async () => {
        const options = await taskService.attempts.unscheduleOptions({
          attemptId: attempt.id,
          modifiedOn: attempt.modifiedOn,
        });

        await executeApiRequest({
          options,
          toastMessageForManagerApproval: {
            text: "Work order is locked. Manager approval needed.",
            subtext:
              "The work order is locked and needs manager authorization before it can be moved.",
          },
          successToastMessageForManagerApproval: {
            text: "Attempt Unassigned",
          },
          callback: ({ status }: IApiResponseBase<unknown>) => {
            if (status === HTTP_STATUSES.OK) {
              handleLoadEvents();
            }
          },
        });
      });
    },
    [handleLoadEvents]
  );

  const startWorkOrder = useCallback(
    (attempt: IAttemptListItemResponseForWorkOrder) =>
      stateManager.run(async () =>
        taskService.attempts.start({
          data: { attemptId: attempt.id },
          modifiedOn: attempt.modifiedOn,
          toastMessageConfig: {
            success: {
              text: "Work Order Started",
            },
          },
        })
      ),
    []
  );

  const submitWorkOrder = useCallback(
    (attempt: IAttemptListItemResponseForWorkOrder) =>
      stateManager.run(async () =>
        taskService.attempts.submit({
          data: {
            attemptId: attempt.id,
            intention: SUBMIT_STATUSES.COMPLETE,
          },
          modifiedOn: attempt.modifiedOn,
          toastMessageConfig: {
            success: {
              text: "Work Order Submitted",
            },
          },
        })
      ),
    []
  );

  const closeWorkOrder = useCallback(
    (attempt: IAttemptListItemResponseForWorkOrder) =>
      stateManager.run(async () =>
        taskService.attempts.closeout({
          data: { attemptId: attempt.id },
          modifiedOn: attempt.modifiedOn,
          toastMessageConfig: {
            success: {
              text: "Work Order Closed",
            },
          },
        })
      ),
    []
  );

  const holdWorkOrder = useCallback(
    (data: {
      attempt: IAttemptListItemResponseForWorkOrder;
      contactName?: string;
      reason: string;
      description: string;
      followUpDate: string;
      calendarId: string;
    }) =>
      stateManager.run(async () => {
        const options = await taskService.workOrders.getHoldOptions({
          id: data.attempt.assignment?.workOrder?.workOrderId,
          data: {
            id: data.attempt.assignment?.workOrder?.workOrderId,
            contactName: data.contactName || "",
            reason: data.reason,
            description: data.description,
            followUpDate: data.followUpDate,
            calendarId: data.calendarId,
            isOnHold: true,
          },
          modifiedOn: data.attempt.assignment?.workOrder?.modifiedOn,
        });

        const response = await executeApiRequest({
          options,
          noInitialErrorMessage: true,
        });

        if (response.status === HTTP_STATUSES.OK) {
          toastService.success({
            text: "Work Order Hold Successful",
            subtext: "Your work order hold was successful",
          });
        } else if (response.status !== HTTP_STATUSES.MANAGER_APPROVAL_REQUIRE) {
          toastService.error({
            text: "Work Order Hold Failed",
            subtext: "Your work order hold was not sent, please try again",
          });
        }

        return response;
      }),
    [calendarSetConfig, activeCalendarId]
  );

  const resetWorkOrder = useCallback(
    (attempt: IAttemptListItemResponseForWorkOrder, onSuccess?: () => void) => {
      stateManager.run(async () => {
        const options = await taskService.workOrders.getResetOptions({
          id: attempt.assignment?.workOrder?.workOrderId,
          modifiedOn: attempt.assignment?.workOrder?.modifiedOn,
        });

        await executeApiRequest({
          options,
          toastMessageForManagerApproval: {
            text: "Work order is locked. Manager approval needed.",
            subtext:
              "The work order is locked and needs manager authorization before it can be moved.",
          },
          successToastMessageForManagerApproval: {
            text: "Work Order Reset Successful",
          },
          callback: ({ status }: IApiResponseBase<unknown>) => {
            if (status === HTTP_STATUSES.OK) {
              handleLoadEvents();
              onSuccess?.();
            }
          },
        });
      });
    },
    [handleLoadEvents]
  );

  const setWorkOrderLock = useCallback(
    (attempt: IAttemptListItemResponseForWorkOrder, isLocked: boolean) => {
      stateManager.run(async () => {
        const response = await taskService.workOrders.setLock({
          id: attempt.assignment?.workOrder?.workOrderId,
          data: { isLocked },
          modifiedOn: attempt.assignment?.workOrder?.modifiedOn,
          toastMessageConfig: {
            success: {
              text: isLocked ? "Work Order Locked" : "Work Order Unlocked",
            },
          },
        });

        if (response.status === HTTP_STATUSES.OK) {
          handleLoadEvents();
        }
      });
    },
    [handleLoadEvents]
  );

  const updateAttempt = useCallback(
    (attempt?: IAttemptListItemResponseForWorkOrder) => {
      if (!attempt || !attempt?.id) return;

      const showAttempt = ![
        ATTEMPT_STATUSES.NEW,
        ATTEMPT_STATUSES.HOLD,
      ].includes(attempt.attemptStatus);

      setEvents((prev = {}) => {
        const eventsArray = Object.values(prev);
        const currentAttempt = eventsArray.find(
          (event) => event.id === attempt.id
        );

        if (!currentAttempt) return prev;

        if (showAttempt) {
          if (attempt.modifiedOn >= currentAttempt.modifiedOn) {
            return {
              ...prev,
              [attempt.id]: { ...currentAttempt, ...attempt },
            };
          }
          return prev;
        }

        delete prev[attempt.id];

        return prev;
      });
    },
    [events]
  );

  const clearWorkOrderSchedule = useCallback(
    async (user: IUserListItemResponse) => {
      const options = await taskService.attempts.clearWorkOrderScheduleOptions({
        data: {
          userId: user.id,
          date: date.toISOString(),
        },
      });
      await executeApiRequest({
        options,
        successToastMessageForManagerApproval: {
          text: `${user.name}'s schedule was cleared.`,
        },
        callback: ({ status }: IApiResponseBase<unknown>) => {
          if (status === HTTP_STATUSES.OK) {
            handleLoadEvents();
          }
        },
      });
    },
    [date, handleLoadEvents]
  );

  const setCalendarDate = (newDate: Date | string | Moment) => {
    setDate(getDate(newDate));
  };

  const handleCalendarSearch = useCallback((newValue: string) => {
    setSearch(newValue);
  }, []);

  const resetSearch = useCallback(() => {
    setSearch("");
  }, []);

  const getTenantHolidays = async () => {
    const response = await calendarService.tenantHolidays.getAll();
    if (response.status === HTTP_STATUSES.OK) {
      setTenantHolidays(response.data || []);
    }
  };

  const getCalendarCount = async (
    calendar: ICalendarConfig,
    getter: (params: IApiRequest) => Promise<IApiResponse<IAttempt[]>>
  ) => {
    const response = await getter({
      filters: {
        ...calendar.filter,
        attemptStatus: { eq: ATTEMPT_STATUSES.HOLD },
        ...(calendar.calendarType !== CalendarType.AutoHold && {
          isOrderFollowupDue: false,
        }),
        assignment: {
          ...(calendarSetConfig && {
            [calendarSetConfig?.eventType]: {
              [`${calendarSetConfig?.eventType}TemplateId`]: {
                in: getGUIDArray(
                  (calendar.workTemplateBases || []).map(({ id }) => id)
                ),
              },
              holdCalendarId: {
                type: "guid",
                value: calendar.id,
              },
            },
          }),
        },
      },
      sort: "modifiedOn desc",
      top: 0,
    });
    if (response.status === HTTP_STATUSES.OK) {
      return (response.headers.totalcount as string) || "0";
    }

    return "0";
  };

  const getCalendarCounts = useCallback(() => {
    if (!calendarSetConfig) return;

    const getter =
      calendarSetConfig.eventType === CalendarEventType.WorkOrder
        ? taskService.attempts.getAllWorkOrders
        : taskService.attempts.getAll;
    stateManager.run(async () => {
      const counts = await Promise.all(
        calendarSetConfig?.calendarConfigs
          .filter(
            (calendar: ICalendarConfig) =>
              calendar.calendarType === CalendarType.HoldsList ||
              calendar.calendarType === CalendarType.AutoHold
          )
          .map(async (calendar) => {
            const count = await getCalendarCount(calendar, getter);

            return {
              id: calendar.id,
              count,
            };
          })
      );

      setCalendarCounts(
        counts.reduce((acc, { id, count }) => {
          if (count !== null) {
            acc[id] = count;
          }
          return acc;
        }, {} as Record<string, string>)
      );
    });
  }, [calendarSetConfig]);

  const syncZoomToLocalStorage = useCallback(() => {
    const localZoom = getItemLocal<string>(CALENDAR_ZOOM);
    const parsed = Number(localZoom);
    if (Number.isFinite(parsed)) {
      setZoom(parsed);
    }
  }, []);

  useEffect(() => {
    syncZoomToLocalStorage();
  }, [syncZoomToLocalStorage]);

  const handleForceToNextAttempt = useCallback(
    (attempt: IAttemptListItemResponseForWorkOrder, assigneeId?: string) => {
      const payload = {
        assigneeId,
        calendarConfigId: activeCalendarId,
      };

      stateManager.run(async () => {
        taskService.attempts.forceToNext({
          attempt: {
            id: attempt.id,
            modifiedOn: attempt.modifiedOn,
          },
          data: payload,
          toastMessageConfig: {
            success: {
              text: "Attempt forced to next successfully",
            },
          },
        });
      });
    },
    [activeCalendarId]
  );

  return {
    calendarSet,
    calendarConfig,
    events,
    assignableUsers,
    schedulerUsers,
    activeCalendarId,
    zoom,
    isLoading: stateManager.isBusy(),
    date,
    dateRange,
    search,
    debouncedSearch,
    trayBuckets,
    selectedAttempt,
    calendarCounts,
    verticalTabActiveFilter,
    activeViewRef,
    users,
    tenantHolidays: activeTenantHolidays,
    highlightedAttempts,
    getCalendarCounts,
    updateAttempt,
    highlightAttempts,
    unhighlightAttempts,
    loadUsers,
    startWorkOrder,
    submitWorkOrder,
    closeWorkOrder,
    holdWorkOrder,
    resetWorkOrder,
    setWorkOrderLock,
    handleCalendarSearch,
    setCalendarDate,
    setZoom,
    onAttemptsUpdate,
    unscheduleAttempt,
    handleLoadEvents,
    onDateRangeChange,
    getTrayDataForCalendar,
    getTenantHolidays,
    resetTrays,
    resetSearch,
    clearWorkOrderSchedule,
    setSelectedAttempt,
    setVerticalTabActiveFilter,
    setActiveView,
    handleForceToNextAttempt,
    assigneeModeUser,
    isScheduler,
  };
};

interface IUseCalendarProviderViewModelProps {
  calendarSetId?: string | null;
  calendarId?: string | null;
  selectedUser?: IUserListItemResponse | null;
}
